import {IYii} from "../../../globals";
import {Injectable, signal} from '@angular/core';
import {AppDialogService} from './AppDialogService';
import dayjs from 'dayjs';
import {
  BcGqlService,
  IGQLFormBuilderScheduleSettings,
  IGQLFormOverdueSettings,
  IGQLFormSettingsDueTime,
  IGQLResponse
} from '@betterchains/bc-gql';
import {AssignmentService} from './AssignmentService';
import {HttpResponse} from '@angular/common/http';

declare var Yii: IYii;

export const DISABLE_DUE_TIME = false
export const MONTHLY_REPEAT_SETTINGS_DAY_OF_MONTH = 'day_of_month'
export const END_CONDITION_NEVER = 'never';
export const NO_REPEAT_PERIOD = 'no_repeat';
export const DAILY_REPEAT_PERIOD = 'daily';
export const MONTHLY_REPEAT_PERIOD = 'monthly';

export interface ISetDaysOfWeek {
  title: string,
  value: string,
  checked: boolean
}

export interface ITitleValue {
  title: string,
  value: string
}

export const setDaysOfWeek: ISetDaysOfWeek[] = [
  {title: 'Sunday', value: 'sunday', checked: false},
  {title: 'Monday', value: 'monday', checked: false},
  {title: 'Tuesday', value: 'tuesday', checked: false},
  {title: 'Wednesday', value: 'wednesday', checked: false},
  {title: 'Thursday', value: 'thursday', checked: false},
  {title: 'Friday', value: 'friday', checked: false},
  {title: 'Saturday', value: 'saturday', checked: false}
]

export const setOfPeriods: ITitleValue[] = [
  {title: 'No Repeat', value: 'no_repeat'},
  {title: 'Daily', value: 'daily'},
  {title: 'Weekly', value: 'weekly'},
  {title: 'Monthly', value: 'monthly'}
]

export const setOfMonthSettings: ITitleValue[] = [
  {title: 'Day Of Month', value: 'day_of_month'},
  {title: 'Day Of Week', value: 'day_of_week'}
]

export interface IScheduleSettings {
  schedule: IGQLFormBuilderScheduleSettings
  overdue: IGQLFormOverdueSettings,
  due_times: IGQLFormSettingsDueTime[]
}

export interface IScheduleSettingsCache {
  chain_id: number,
  branch_id: number,
  form_id: number,
  settings: IScheduleSettings,
  changed: boolean
}

const scheduleDefaults: IGQLFormBuilderScheduleSettings = {
  branch_id: 0,
  chain_id: 0,
  enable_due_time: false,
  end_condition: 'never',
  end_condition_param_occurrences: null,
  end_condition_param_on_date: null,
  form_id: 0,
  friday: false,
  monday: false,
  monthly_repeat_settings: '',
  repeat_period: 'daily',
  saturday: false,
  start_day: dayjs().format('YYYY-MM-DD'),
  sunday: false,
  thursday: false,
  tuesday: false,
  wednesday: false,
  weekly_repeat_settings: JSON.stringify([
    {"title": "Sunday", "value": "sunday", "checked": true},
    {"title": "Monday", "value": "monday", "checked": false},
    {
      "title": "Tuesday",
      "value": "tuesday",
      "checked": true
    },
    {
      "title": "Wednesday",
      "value": "wednesday",
      "checked": false
    },
    {"title": "Thursday", "value": "thursday", "checked": true},
    { "title": "Friday","value": "friday","checked": false},
    {"title": "Saturday", "value": "saturday", "checked": true}
  ]),
}

@Injectable({
  providedIn: 'root'
})
export class ScheduleService {

  public settings: IScheduleSettings

  private cached: IScheduleSettingsCache[] = []

  public loading = signal(false)

  constructor(
    private asvc: AssignmentService,
    private dialogs: AppDialogService,
    private gql: BcGqlService,
  ) {
  }

  public Load() {
    const chainId = this.asvc.GetCurrentChainId()
    const branchId = this.asvc.GetCurrentBranchId()

    if (!this.asvc.IsChainAssigned() || !this.asvc.IsBranchAssigned()) {
      return;
    }

    const i = this.cached.findIndex(lf => lf.chain_id == chainId && lf.branch_id == branchId);
    if (i >= 0) {
      this.settings = this.cached[i].settings;
      return;
    }


    this.loading.set(true)
    this.gql.query(
      //language=gql
      `
        query GetScheduleSettings($chainId: Int, $branchId: Int, $formId: Int) {
          forms {
            form_schedule(chain_id: $chainId, branch_id: $branchId, form_id: $formId) {
              start_day
              repeat_period
              weekly_repeat_settings
              monthly_repeat_settings
              end_condition
              end_condition_param_on_date
              end_condition_param_occurrences
              enable_due_time
              sunday
              monday
              tuesday
              wednesday
              thursday
              friday
              saturday
            }
            form_due_times(form_id: $formId, chain_id: $chainId, branch_id: $branchId) {
              due_time
              id
            }
            form_overdue(form_id: 3, chain_id: 16, branch_id: 0) {
              id
              is_enabled
              overdue
              subscribers {
                id
              }
            }
          }
        }
      `,
      {
        formId: this.asvc.formId,
        chainId,
        branchId
      }
    ).pipe().subscribe({
      next: (r: HttpResponse<IGQLResponse<any>>) => {
        this.loading.set(false)
        const settings = {
          schedule: r.body.data.forms.form_schedule ?? scheduleDefaults,
          due_times: r.body.data.forms.form_due_times,
          overdue: r.body.data.forms.form_overdue,
        }
        this.settings = settings;
        this.cached.push({
          chain_id: chainId,
          branch_id: branchId,
          form_id: this.asvc.formId,
          settings,
          changed: false
        })
      }
    })
  }

  clearDueTimeValue() {
    if (!this.settings.schedule.enable_due_time) {
      this.settings.due_times = [];

      if (this.settings.overdue.is_enabled) {
        this.settings.overdue.is_enabled = false;
      }
    } else {
      this.AddNewDueTime();
    }
  }

  AddNewDueTime() {
    const timeObject: IGQLFormSettingsDueTime = {
      id: 0,
      chain_id: 0,
      branch_id: 0,
      form_id: 0,
      due_time: dayjs('1970-01-01 12:00:00').format('h:mm A')
    };

    this.settings.due_times.push(timeObject);
  }

  deleteDueTime(index: number) {
    if (this.settings.due_times.length > 1) {
      this.settings.due_times.splice(index, 1);
    } else {
      this.dialogs.Alert(
        Yii.t("app_js", 'Error'),
        Yii.t("be_forms", 'At least one Due Time needs to be set if the "Enable Due Time" is checked. Otherwise uncheck "Enable Due Time"')
      )
    }
  }

  changePeriod() {

    this.settings.schedule.end_condition = (this.settings.schedule.repeat_period !== NO_REPEAT_PERIOD) ?
      END_CONDITION_NEVER : '';

    switch (this.settings.schedule.repeat_period) {
      case MONTHLY_REPEAT_PERIOD:
        this.settings.schedule.monthly_repeat_settings = MONTHLY_REPEAT_SETTINGS_DAY_OF_MONTH;
        break;
      default:
        this.settings.schedule.monthly_repeat_settings = '';
    }

    this.clearParams();

  }

  clearParams() {
    const todayDate = new Date();

    const END_CONDITION_ON_DATE = 'on_date';

    this.settings.schedule.end_condition_param_occurrences =
      (this.settings.schedule.end_condition === 'occurrences_number') ? 1 : 0;

    if (this.settings.schedule.end_condition === END_CONDITION_ON_DATE) {
      this.settings.schedule.end_condition_param_on_date = todayDate.toISOString().slice(0, 10);
    } else {
      this.settings.schedule.end_condition_param_on_date = '';
    }
  }
}
